import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_AUTISM_SPECTRUM,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import SendToFriendButton from "../../components/send-to-friend-button";
import ShareButton from "../../components/share-button";
import {
  getAutismAdultReportPageImage,
  getCookie,
  getEnneagramBannerImage,
} from "../../utils/utility-functions";

import ExpandableSection from "../../components/expandable-collapsible/component";
import { APP_NAVIGATION, auctism_adult, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const AutismSpectrumReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_AUTISM_SPECTRUM[currentLocale] ||
            MODEL_TYPE_AUTISM_SPECTRUM[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const bannerImage = getEnneagramBannerImage();
  const interpretation = contentData?.interpretation;
  const overallObject = contentData?.overall;
  const howToUse = contentData?.howToUse;
  const nextStepsContent = contentData?.nextSteps;
  const disclaimerContent = contentData?.Disclaimer;
  const result1ImageText = "autism-1";
  const result2ImageText = "autism-2";
  const result1Image = getAutismAdultReportPageImage(result1ImageText);
  const result2Image = getAutismAdultReportPageImage(result2ImageText);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />
        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            {/* Title & Button Wrapper */}
            <div className="flex flex-col md:flex-row items-start justify-between gap-4 pr-10 md:pr-16">
              {/* Title */}
              <h2
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-4xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CallToActionSection = ({ onClick1, onClick2 }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.testMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.discoverMessage")}
            </p>
            <TakeTestButton onClick={onClick1} />
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.bigFiveMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.strengthMesssge")}
            </p>
            <TakeTestButton onClick={onClick2} />
          </div>
        </div>
      </section>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
      >
        <defs>
          <filter
            id={`shadow-${character || "bullet"}-${actualSize}`}
            x="-30%"
            y="-30%"
            width="160%"
            height="160%"
          >
            <feGaussianBlur
              stdDeviation="3"
              in="SourceAlpha"
              result="blurOut"
            />

            <feOffset dx="0" dy="2" in="blurOut" result="offsetBlur" />

            <feComponentTransfer in="offsetBlur" result="coloredShadow">
              <feFuncA type="linear" slope="0.3" />
            </feComponentTransfer>

            <feMerge>
              <feMergeNode in="coloredShadow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          filter={`url(#shadow-${character || "bullet"}-${actualSize})`}
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  const NextStepsCard = ({ nextStepsContent }) => {
    // Check if content data exists
    if (!nextStepsContent || !Array.isArray(nextStepsContent?.Content)) {
      return null;
    }

    return (
      <div className="p-6 rounded-2xl shadow-lg bg-purple-50 flex flex-col h-full">
        <div className="flex flex-row gap-3 items-center">
          <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
            <CircleIcon character="?" size={22} />
          </div>
          <h2 className="text-xl font-semibold text-gray-700">
            {nextStepsContent.RootHeading || "Next Steps"}
          </h2>
        </div>

        <div className="space-y-4 mt-4 pl-10">
          {nextStepsContent?.Content?.map((section, index) => {
            // Skip if section is invalid
            if (!section || typeof section !== "object") return null;

            return (
              <div key={index} className="mb-3">
                <h3 className="font-bold text-gray-700">
                  {section?.Heading || `Section ${index + 1}`}
                </h3>
                <div className="mt-1 space-y-2">
                  {Array.isArray(section?.Content) &&
                    section.Content.map((contentItem, contentIndex) => {
                      // Skip if content item is invalid
                      if (contentItem === null || contentItem === undefined)
                        return null;

                      // Special handling for the Learn More section with bullet points
                      if (
                        section?.Heading === "Learn More" &&
                        contentIndex > 0 &&
                        contentIndex < 4
                      ) {
                        return (
                          <div
                            key={contentIndex}
                            className="flex items-start ml-4 gap-2"
                          >
                            <div className="shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                              <CircleIcon character="" size={10} />
                            </div>
                            <p className="text-gray-700">{contentItem}</p>
                          </div>
                        );
                      } else if (
                        section?.Heading === "Reflect on Your Results"
                      ) {
                        return (
                          <p
                            key={contentIndex}
                            className="text-gray-700 text-base leading-relaxed"
                            style={{ fontStyle: "italic" }}
                          >
                            {contentItem}
                          </p>
                        );
                      } else {
                        return (
                          <p
                            key={contentIndex}
                            className="text-gray-700 text-base leading-relaxed"
                          >
                            {contentItem}
                          </p>
                        );
                      }
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const DisclaimerCard = ({ disclaimerContent }) => {
    const paragraphs = disclaimerContent?.Content?.split("\n");
    const highlightText = (text) => {
      const highlightedPhrases = ["screening tool", "not a diagnostic tool"];

      let processedText = text;
      highlightedPhrases.forEach((phrase) => {
        if (text.includes(phrase)) {
          processedText = processedText.replace(
            phrase,
            `<span class="text-blue-600 font-medium">${phrase}</span>`
          );
        }
      });

      return processedText;
    };

    return (
      <div className="p-6 rounded-2xl shadow-lg bg-green-50 flex flex-col h-full">
        <div className="flex flex-row gap-3 items-center mb-4">
          <h2 className="text-xl font-semibold text-emerald-800">
            {disclaimerContent?.RootHeading}
          </h2>
        </div>

        <div className="space-y-4">
          {paragraphs?.map((paragraph, index) => (
            <p
              key={index}
              className="text-gray-700 text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: highlightText(paragraph) }}
            />
          ))}
        </div>
      </div>
    );
  };

  const Table = ({ averagePercentage, level }) => (
    <div className="w-full bg-white">
      <table className="w-full border-collapse table-fixed">
        <thead>
          <tr className="border-b border-gray-300">
            <th className="p-3 text-left font-bold tracking-wider text-lg">
              {t("report.autism_spectrum.your_scores")}
            </th>
            <th className="p-3 text-right font-bold tracking-wider text-lg">
              {t("report.autism_spectrum.results")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          <tr className="border-b border-gray-300">
            <td className="p-3 text-sm font-medium text-gray-900">
              {t("report.autism_spectrum.total_scores")}
            </td>
            <td className="p-3 text-right text-sm font-semibold text-[#805fe6]">
              {averagePercentage}
            </td>
          </tr>
          <tr className="border-b border-gray-300">
            <td className="p-3 text-sm font-medium text-gray-900 w-1/2">
              {t("report.autism_spectrum.overall_result")}
            </td>
            <td className="p-3 text-right text-sm font-semibold text-[#805fe6]">
              {level}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  function transformUserTraitsData(data) {
    if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

    const result = data.reduce((acc, item) => {
      if (
        item?.trait?.title &&
        typeof item.percentage === "number" &&
        typeof item.score === "number"
      ) {
        acc[item.trait.title] = {
          percentage: item.percentage,
          score: item.score,
        };
      }
      return acc;
    }, {});

    const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

    return { result, totalScore };
  }

  function convertToSnakeCaseObject(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return {};

    const toSnakeCase = (str) =>
      str && typeof str === "string"
        ? str
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/_+$/, "")
        : "";

    return arr.reduce((acc, item) => {
      if (!item || typeof item !== "object" || !item.EnglishCode) return acc;

      const key = toSnakeCase(item.EnglishCode.split(":")[0]);
      if (key) acc[key] = item;

      return acc;
    }, {});
  }

  function categorizeUserTraits(
    interpretationObject,
    updatedUserTraits,
    overallObject
  ) {
    if (!interpretationObject || !updatedUserTraits?.result || !overallObject)
      return {};

    function getOverAllLevelDetails(score, content) {
      if (score >= 40 && score <= 99) {
        return {
          level: content[0]?.Heading,
          details: content[0]?.Content,
        };
      } else if (score >= 100 && score <= 149) {
        return {
          level: content[1]?.Heading,
          details: content[1]?.Content,
        };
      } else if (score >= 150 && score <= 200) {
        return {
          level: content[2]?.Heading,
          details: content[2]?.Content,
        };
      } else {
        return {
          level: "Invalid",
          details: "Score is out of expected range (10–50).",
        };
      }
    }

    const getLevel = (score, content) => {
      if (score >= 10 && score <= 24) {
        return {
          level: content[1]?.Heading,
          details: content[0]?.Content,
        };
      } else if (score >= 25 && score <= 37) {
        return {
          level: content[2]?.Heading,
          details: content[1]?.Content,
        };
      } else if (score >= 38 && score <= 50) {
        return {
          level: content[2]?.Heading,
          details: content[2]?.Content,
        };
      } else {
        return {
          level: "Invalid",
          details: "Score is out of expected range (10–50).",
        };
      }
    };

    let categorizedResults = {};

    for (const key in updatedUserTraits?.result) {
      if (interpretationObject[key]) {
        const { score } = updatedUserTraits?.result[key];
        const { level, details } = getLevel(
          score,
          interpretationObject[key].Content
        );
        categorizedResults[key] = {
          Heading: interpretationObject[key].Heading.replace("{score}", score),
          score,
          level: level.split(" ")[0],
          details,
        };
      }
    }

    const overallScore = updatedUserTraits?.totalScore;

    const { level, details } = getOverAllLevelDetails(
      overallScore,
      overallObject.Content
    );
    categorizedResults.overall = {
      Heading: overallObject.RootHeading,
      score: overallScore,
      level: level.split(" ")[0],
      details,
    };

    return categorizedResults;
  }

  const interpretationObject = convertToSnakeCaseObject(
    interpretation?.Content
  );

  const updatedUserTraits = transformUserTraitsData(userTraits);

  const categorizedData = categorizeUserTraits(
    interpretationObject,
    updatedUserTraits,
    overallObject
  );

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full mb-6">
                    <div className="w-full sm:w-[60%] px-8">
                      <p className="text-gray-700 mt-4">
                        {contentData?.overall?.FlavorText?.replace(
                          "{score}",
                          updatedUserTraits?.totalScore
                        ).split(":")[0] + "."}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[3fr_2fr] gap-6 sm:gap-8 items-stretch my-4 h-full">
                      <div className="w-full sm:px-2">
                        <div className="mt-8">
                          <Table
                            averagePercentage={categorizedData?.overall?.score}
                            level={categorizedData?.overall?.level}
                          />
                        </div>
                      </div>
                      {/* Right Section: Image */}
                      <div className="w-full px-8 flex items-center justify-center h-full">
                        <img
                          src={result1Image}
                          alt="BannerImage"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                    {/* Score Explanation */}
                    <div className="w-full px-8 mt-6">
                      <h2 className="text-xl font-semibold text-gray-700 mb-4">
                        {categorizedData?.overall?.Heading}
                      </h2>
                      <DescriptiveCard
                        heading={categorizedData?.overall?.level}
                        overview={categorizedData?.overall?.details}
                        bgClr={"bg-blue-50"}
                        textClr={"text-blue-800"}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.yourScore?.RootHeading}
                      color="purple"
                    />
                  }
                >
                  <div className="w-full px-8 py-6">
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed my-4">
                      {contentData?.interpretation?.FlavorText}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 items-stretch">
                      {[
                        {
                          key: "social_interaction_and_communication",
                          bgClr: "bg-rose-50",
                          textClr: "text-rose-800",
                        },
                        {
                          key: "repetitive_behaviors_and_routines",
                          bgClr: "bg-yellow-50",
                          textClr: "text-yellow-800",
                        },
                        {
                          key: "sensory_sensitivities",
                          bgClr: "bg-green-50",
                          textClr: "text-green-800",
                        },
                        {
                          key: "cognitive_and_behavioral_patterns",
                          bgClr: "bg-violet-50",
                          textClr: "text-violet-800",
                        },
                      ].map(({ key, bgClr, textClr }) => (
                        <div key={key} className="flex flex-col h-full">
                          <h2 className="text-lg font-semibold text-gray-700 mt-6 mb-3">
                            {categorizedData?.[key]?.Heading?.split(":")[0]}
                          </h2>

                          <DescriptiveCard
                            heading={categorizedData?.[key]?.level}
                            overview={categorizedData?.[key]?.details}
                            bgClr={bgClr}
                            textClr={textClr}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner title={howToUse?.RootHeading} color="red" />
                  }
                >
                  <div className="w-full px-8 py-6">
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed my-4">
                      {howToUse?.Content}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-[3fr_2fr] gap-6 sm:gap-8 items-stretch my-4 h-full">
                      <div className="h-full flex">
                        <NextStepsCard
                          nextStepsContent={nextStepsContent}
                          className="h-full w-full"
                        />
                      </div>
                      <div className="flex items-center justify-center h-full">
                        <img
                          src={result2Image}
                          alt="BannerImage"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    <DisclaimerCard disclaimerContent={disclaimerContent} />
                  </div>
                </ExpandableSection>
              </section>
              <CallToActionSection
                onClick1={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
                onClick2={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
              />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AutismSpectrumReport;
